import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus, FaSave, FaTimes, FaSpinner, FaCheck, FaExclamationTriangle, FaClock, FaExclamationCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';
import { listingService } from '../services/listingService';
import { supabase } from '../lib/supabase';
import { USER_ROLES } from '../constants/roles';
import toast from 'react-hot-toast';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  min-height: calc(100vh - 200px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid ${props => props.theme.colors.border};
  overflow-x: auto;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textSecondary};
  border-bottom: 3px solid ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
`;

const Section = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  color: ${props => props.theme.colors.text};
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  margin: 0;
  font-size: 1.3rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Button = styled.button`
  background-color: ${props => props.$variant === 'secondary' ? '#6c757d' : '#8B5A96'};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: ${props => props.$variant === 'secondary' ? '#5a6268' : '#6B2E7A'};
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.45rem 0.75rem;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;
  
  &:hover {
    background-color: #c82333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 0.3rem;
  color: ${props => props.theme.colors.text};
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 0.9rem;
  box-sizing: border-box;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
  
  &:disabled {
    opacity: 0.6;
    background-color: ${props => props.theme.colors.surfaceAlt};
  }
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.45rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 5px;
  font-size: 0.9rem;
  min-height: 80px;
  box-sizing: border-box;
  resize: vertical;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text};
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 0.9rem;
  box-sizing: border-box;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const Card = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 1rem;
  background-color: ${props => props.theme.colors.surfaceAlt};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const CardTitle = styled.h3`
  margin: 0;
  color: ${props => props.theme.colors.text};
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const CardSubtitle = styled.p`
  margin: 0.25rem 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const CardText = styled.p`
  margin: 0.5rem 0;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$danger ? '#dc3545' : '#8B5A96'};
  cursor: pointer;
  padding: 0.25rem;
  font-size: 1rem;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.7;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: #8B5A96;
`;

const SkillBadge = styled.span`
  display: inline-block;
  background-color: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ListingCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 1rem;
  background-color: ${props => props.theme.colors.surface};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 2px 8px ${props => props.theme.colors.shadow};
    transform: translateY(-2px);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.$status) {
      case 'active': return props.theme.colors.successBg;
      case 'pending_moderation': return props.theme.colors.warningBg;
      case 'rejected': return props.theme.colors.dangerBg;
      case 'draft': return props.theme.colors.surfaceAlt;
      case 'archived': return '#6c757d';
      default: return props.theme.colors.surfaceAlt;
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'active': return props.theme.colors.successText;
      case 'pending_moderation': return props.theme.colors.warningText;
      case 'rejected': return props.theme.colors.dangerText;
      case 'draft': return props.theme.colors.textSecondary;
      case 'archived': return 'white';
      default: return props.theme.colors.textSecondary;
    }
  }};
`;

const ExpiryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 0.5rem;
  background-color: ${props => {
    if (props.$isExpired) return '#dc3545';
    if (props.$isExpiringSoon) return '#ffc107';
    return '#28a745';
  }};
  color: ${props => props.$isExpired || props.$isExpiringSoon ? '#000' : '#fff'};
`;

const ModerationCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 10px ${props => props.theme.colors.shadow};
  border-left: 4px solid ${props => props.theme.colors.warning};
`;

const ModerationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const ApproveButton = styled(Button)`
  background-color: #28a745;
  
  &:hover {
    background-color: #218838;
  }
`;

const RejectButton = styled(Button)`
  background-color: #dc3545;
  
  &:hover {
    background-color: #c82333;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 10px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  
  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
`;


const skills = [
  "Agriculture & Farming",
  "Communication & Public Relations",
  "Construction & Trades",
  "Customer Service & Support",
  "Data & Analytics",
  "Design & Creative Arts",
  "Education & Training",
  "Engineering & Technical",
  "Environmental & Sustainability",
  "Finance & Accounting",
  "Healthcare & Medical",
  "Hospitality & Tourism",
  "Human Resources & People Management",
  "Legal & Compliance",
  "Logistics & Supply Chain",
  "Manufacturing & Production",
  "Marketing & Sales",
  "Media & Entertainment",
  "Personal Services & Lifestyle",
  "Project Management",
  "Science & Research",
  "Sports & Fitness",
  "Technology & IT",
  "Writing & Content Creation"
];

const Profile = () => {
  const navigate = useNavigate();
  const { user, profile, refreshProfile, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState([]);
  
  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    display_name: '',
    bio: '',
    phone: '',
    location: { province: '', city: '' },
    skills: []
  });
  
  // Experience editing
  const [experiences, setExperiences] = useState([]);
  const [editingExperience, setEditingExperience] = useState(null);
  const [newExperience, setNewExperience] = useState(null);
  
  // Qualifications editing
  const [qualifications, setQualifications] = useState([]);
  const [editingQualification, setEditingQualification] = useState(null);
  const [newQualification, setNewQualification] = useState(null);
  
  // Certifications editing
  const [certifications, setCertifications] = useState([]);
  const [editingCertification, setEditingCertification] = useState(null);
  const [newCertification, setNewCertification] = useState(null);
  
  // Admin moderation state
  const [pendingListings, setPendingListings] = useState([]);
  const [allListings, setAllListings] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModal, setRejectModal] = useState({ show: false, listing: null, reason: '' });

  useEffect(() => {
    if (profile) {
      setProfileData({
        display_name: profile.display_name || '',
        bio: profile.bio || '',
        phone: profile.phone || '',
        location: profile.location || { province: '', city: '' },
        skills: profile.skills || []
      });
      setExperiences(profile.experience || []);
      setQualifications(profile.qualifications || []);
      setCertifications(profile.certifications || []);
    }
  }, [profile]);

  useEffect(() => {
    if (activeTab === 'listings') {
      loadUserListings();
    } else if (activeTab === 'moderation' && profile?.role_type === USER_ROLES.ADMIN) {
      loadPendingListings();
    } else if (activeTab === 'all-listings' && profile?.role_type === USER_ROLES.ADMIN) {
      loadAllListings();
    }
  }, [activeTab, profile]);

  const loadUserListings = async () => {
    setLoading(true);
    try {
      const { listings: userListings } = await listingService.getUserListings(user.id);
      setListings(userListings || []);
    } catch (error) {
      console.error('Error loading listings:', error);
      toast.error('Failed to load your listings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await updateProfile({
        ...profileData,
        experience: experiences,
        qualifications: qualifications,
        certifications: certifications
      });
      await refreshProfile();
      setEditingProfile(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExperience = () => {
    setNewExperience({
      title: '',
      company: '',
      location: '',
      start_date: '',
      end_date: '',
      description: '',
      current: false
    });
  };

  const handleSaveExperience = () => {
    if (newExperience) {
      setExperiences([...experiences, { ...newExperience, id: Date.now() }]);
      setNewExperience(null);
    }
  };

  const handleDeleteExperience = (id) => {
    setExperiences(experiences.filter(exp => exp.id !== id));
  };

  const handleAddQualification = () => {
    setNewQualification({
      degree: '',
      institution: '',
      field: '',
      graduation_year: '',
      description: ''
    });
  };

  const handleSaveQualification = () => {
    if (newQualification) {
      setQualifications([...qualifications, { ...newQualification, id: Date.now() }]);
      setNewQualification(null);
    }
  };

  const handleDeleteQualification = (id) => {
    setQualifications(qualifications.filter(qual => qual.id !== id));
  };

  const handleAddCertification = () => {
    setNewCertification({
      name: '',
      issuer: '',
      issue_date: '',
      expiry_date: '',
      credential_id: '',
      credential_url: ''
    });
  };

  const handleSaveCertification = () => {
    if (newCertification) {
      setCertifications([...certifications, { ...newCertification, id: Date.now() }]);
      setNewCertification(null);
    }
  };

  const handleDeleteCertification = (id) => {
    setCertifications(certifications.filter(cert => cert.id !== id));
  };

  const handleEditListing = (listing) => {
    // Navigate to the appropriate edit page based on listing type
    if (listing.type === 'hustle') {
      navigate('/share-hustle', { state: { editListing: listing } });
    } else {
      navigate('/share-gig', { state: { editListing: listing } });
    }
  };

  const handleDeleteListing = async (listing) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${listing.title}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setLoading(true);
    try {
      await listingService.deleteListing(listing.id);
      toast.success('Listing deleted successfully');
      // Reload listings
      await loadUserListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast.error('Failed to delete listing');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSkill = (skill) => {
    setProfileData(prev => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter(s => s !== skill)
        : [...prev.skills, skill]
    }));
  };

  // Admin moderation functions
  const loadPendingListings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          profiles!owner_id (
            display_name,
            email
          )
        `)
        .eq('status', 'pending_moderation')
        .order('submitted_at', { ascending: true });

      if (error) throw error;
      setPendingListings(data || []);
    } catch (error) {
      console.error('Error loading pending listings:', error);
      toast.error('Failed to load pending listings');
    } finally {
      setLoading(false);
    }
  };

  const loadAllListings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          profiles!owner_id (
            display_name,
            email
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAllListings(data || []);
    } catch (error) {
      console.error('Error loading all listings:', error);
      toast.error('Failed to load all listings');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminDeleteListing = async (listing) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${listing.title}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setLoading(true);
    try {
      await listingService.deleteListing(listing.id);
      toast.success('Listing deleted successfully');
      // Reload all listings
      await loadAllListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast.error('Failed to delete listing');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveListing = async (listing) => {
    setActionLoading(listing.id);
    try {
      const { error: listingError } = await supabase
        .from('listings')
        .update({
          status: 'active',
          approved_at: new Date().toISOString()
        })
        .eq('id', listing.id);

      if (listingError) throw listingError;

      const { error: decisionError } = await supabase
        .from('moderation_decisions')
        .insert({
          listing_id: listing.id,
          moderator_id: profile.id,
          status: 'approved'
        });

      if (decisionError) throw decisionError;

      toast.success('Listing approved successfully!');
      setPendingListings(prev => prev.filter(l => l.id !== listing.id));
    } catch (error) {
      console.error('Error approving listing:', error);
      toast.error('Failed to approve listing');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectListing = async () => {
    if (!rejectModal.listing || !rejectModal.reason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setActionLoading(rejectModal.listing.id);
    try {
      const { error: listingError } = await supabase
        .from('listings')
        .update({ status: 'rejected' })
        .eq('id', rejectModal.listing.id);

      if (listingError) throw listingError;

      const { error: decisionError } = await supabase
        .from('moderation_decisions')
        .insert({
          listing_id: rejectModal.listing.id,
          moderator_id: profile.id,
          status: 'rejected',
          reason: rejectModal.reason
        });

      if (decisionError) throw decisionError;

      toast.success('Listing rejected');
      setPendingListings(prev => prev.filter(l => l.id !== rejectModal.listing.id));
      setRejectModal({ show: false, listing: null, reason: '' });
    } catch (error) {
      console.error('Error rejecting listing:', error);
      toast.error('Failed to reject listing');
    } finally {
      setActionLoading(null);
    }
  };

  if (!user) {
    return (
      <Container>
        <Section>
          <h2>Please log in to view your profile</h2>
        </Section>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container>
        <LoadingContainer>
          <FaSpinner size={24} style={{ animation: 'spin 1s linear infinite' }} />
          <span style={{ marginLeft: '1rem' }}>Loading profile...</span>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <TabContainer>
        <Tab $active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
          My Profile
        </Tab>
        <Tab $active={activeTab === 'listings'} onClick={() => setActiveTab('listings')}>
          My Posts
        </Tab>
        {profile?.role_type === USER_ROLES.ADMIN && (
          <>
            <Tab $active={activeTab === 'moderation'} onClick={() => setActiveTab('moderation')}>
              Moderation
            </Tab>
            <Tab $active={activeTab === 'all-listings'} onClick={() => setActiveTab('all-listings')}>
              All Listings
            </Tab>
          </>
        )}
      </TabContainer>

      {activeTab === 'profile' && (
        <>
          <Section>
            <SectionHeader>
              <SectionTitle>Basic Information</SectionTitle>
              {!editingProfile ? (
                <Button onClick={() => setEditingProfile(true)}>
                  <FaEdit /> Edit Profile
                </Button>
              ) : (
                <ButtonGroup>
                  <Button onClick={handleSaveProfile} disabled={loading}>
                    <FaSave /> Save
                  </Button>
                  <Button $variant="secondary" onClick={() => setEditingProfile(false)}>
                    <FaTimes /> Cancel
                  </Button>
                </ButtonGroup>
              )}
            </SectionHeader>

            {editingProfile ? (
              <>
                <FormGroup>
                  <Label>Display Name</Label>
                  <Input
                    value={profileData.display_name}
                    onChange={(e) => setProfileData({ ...profileData, display_name: e.target.value })}
                    placeholder="Your name"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Email</Label>
                  <Input value={profile.email} disabled />
                </FormGroup>
                <FormGroup>
                  <Label>Bio</Label>
                  <TextArea
                    value={profileData.bio}
                    onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                    placeholder="Tell us about yourself..."
                  />
                </FormGroup>
                <Grid>
                  <FormGroup>
                    <Label>Phone</Label>
                    <Input
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      placeholder="+27 83 298 8388"
                    />
                  </FormGroup>
                </Grid>
                <Grid>
                  <FormGroup>
                    <Label>Province</Label>
                    <Input
                      value={profileData.location.province}
                      onChange={(e) => setProfileData({ 
                        ...profileData, 
                        location: { ...profileData.location, province: e.target.value }
                      })}
                      placeholder="Western Cape"
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      value={profileData.location.city}
                      onChange={(e) => setProfileData({ 
                        ...profileData, 
                        location: { ...profileData.location, city: e.target.value }
                      })}
                      placeholder="Cape Town"
                    />
                  </FormGroup>
                </Grid>
              </>
            ) : (
              <>
                <p><strong>Name:</strong> {profile.display_name || 'Not set'}</p>
                <p><strong>Email:</strong> {profile.email}</p>
                {profile.bio && <p><strong>Bio:</strong> {profile.bio}</p>}
                {profile.phone && <p><strong>Phone:</strong> {profile.phone}</p>}
                {profile.location && (profile.location.city || profile.location.province) && (
                  <p><strong>Location:</strong> {profile.location.city}{profile.location.city && profile.location.province ? ', ' : ''}{profile.location.province}</p>
                )}
                <p><strong>Member Since:</strong> {new Date(profile.created_at).toLocaleDateString()}</p>
              </>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>Skills</SectionTitle>
            </SectionHeader>
            {editingProfile ? (
              <div>
                {skills.map(skill => (
                  <label key={skill} style={{ display: 'inline-block', marginRight: '1rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={profileData.skills.includes(skill)}
                      onChange={() => handleToggleSkill(skill)}
                      style={{ marginRight: '0.3rem' }}
                    />
                    {skill}
                  </label>
                ))}
              </div>
            ) : (
              <div>
                {profile.skills && profile.skills.length > 0 ? (
                  profile.skills.map(skill => <SkillBadge key={skill}>{skill}</SkillBadge>)
                ) : (
                  <p>No skills added yet</p>
                )}
              </div>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>Experience</SectionTitle>
              {editingProfile && (
                <Button onClick={handleAddExperience}>
                  <FaPlus /> Add Experience
                </Button>
              )}
            </SectionHeader>
            <Grid>
              {experiences.map(exp => (
                <Card key={exp.id}>
                  <CardHeader>
                    <div>
                      <CardTitle>{exp.title}</CardTitle>
                      <CardSubtitle>{exp.company}</CardSubtitle>
                      <CardSubtitle>{exp.location}</CardSubtitle>
                      <CardSubtitle>
                        {exp.start_date} - {exp.current ? 'Present' : exp.end_date}
                      </CardSubtitle>
                    </div>
                    {editingProfile && (
                      <IconButton $danger onClick={() => handleDeleteExperience(exp.id)}>
                        <FaTrash />
                      </IconButton>
                    )}
                  </CardHeader>
                  {exp.description && <CardText>{exp.description}</CardText>}
                </Card>
              ))}
            </Grid>
            {newExperience && (
              <Card style={{ marginTop: '1rem' }}>
                <FormGroup>
                  <Label>Job Title</Label>
                  <Input
                    value={newExperience.title}
                    onChange={(e) => setNewExperience({ ...newExperience, title: e.target.value })}
                  />
                </FormGroup>
                <Grid>
                  <FormGroup>
                    <Label>Company</Label>
                    <Input
                      value={newExperience.company}
                      onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Location</Label>
                    <Input
                      value={newExperience.location}
                      onChange={(e) => setNewExperience({ ...newExperience, location: e.target.value })}
                    />
                  </FormGroup>
                </Grid>
                <Grid>
                  <FormGroup>
                    <Label>Start Date</Label>
                    <Input
                      type="month"
                      value={newExperience.start_date}
                      onChange={(e) => setNewExperience({ ...newExperience, start_date: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>End Date</Label>
                    <Input
                      type="month"
                      value={newExperience.end_date}
                      onChange={(e) => setNewExperience({ ...newExperience, end_date: e.target.value })}
                      disabled={newExperience.current}
                    />
                  </FormGroup>
                </Grid>
                <FormGroup>
                  <label style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newExperience.current}
                      onChange={(e) => setNewExperience({ ...newExperience, current: e.target.checked })}
                      style={{ marginRight: '0.5rem' }}
                    />
                    I currently work here
                  </label>
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={newExperience.description}
                    onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })}
                  />
                </FormGroup>
                <ButtonGroup>
                  <Button onClick={handleSaveExperience}>
                    <FaSave /> Save
                  </Button>
                  <Button $variant="secondary" onClick={() => setNewExperience(null)}>
                    <FaTimes /> Cancel
                  </Button>
                </ButtonGroup>
              </Card>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>Qualifications</SectionTitle>
              {editingProfile && (
                <Button onClick={handleAddQualification}>
                  <FaPlus /> Add Qualification
                </Button>
              )}
            </SectionHeader>
            <Grid>
              {qualifications.map(qual => (
                <Card key={qual.id}>
                  <CardHeader>
                    <div>
                      <CardTitle>{qual.degree}</CardTitle>
                      <CardSubtitle>{qual.institution}</CardSubtitle>
                      <CardSubtitle>{qual.field}</CardSubtitle>
                      <CardSubtitle>Graduated: {qual.graduation_year}</CardSubtitle>
                    </div>
                    {editingProfile && (
                      <IconButton $danger onClick={() => handleDeleteQualification(qual.id)}>
                        <FaTrash />
                      </IconButton>
                    )}
                  </CardHeader>
                  {qual.description && <CardText>{qual.description}</CardText>}
                </Card>
              ))}
            </Grid>
            {newQualification && (
              <Card style={{ marginTop: '1rem' }}>
                <Grid>
                  <FormGroup>
                    <Label>Degree/Diploma</Label>
                    <Input
                      value={newQualification.degree}
                      onChange={(e) => setNewQualification({ ...newQualification, degree: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Institution</Label>
                    <Input
                      value={newQualification.institution}
                      onChange={(e) => setNewQualification({ ...newQualification, institution: e.target.value })}
                    />
                  </FormGroup>
                </Grid>
                <Grid>
                  <FormGroup>
                    <Label>Field of Study</Label>
                    <Input
                      value={newQualification.field}
                      onChange={(e) => setNewQualification({ ...newQualification, field: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Graduation Year</Label>
                    <Input
                      type="number"
                      value={newQualification.graduation_year}
                      onChange={(e) => setNewQualification({ ...newQualification, graduation_year: e.target.value })}
                    />
                  </FormGroup>
                </Grid>
                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={newQualification.description}
                    onChange={(e) => setNewQualification({ ...newQualification, description: e.target.value })}
                  />
                </FormGroup>
                <ButtonGroup>
                  <Button onClick={handleSaveQualification}>
                    <FaSave /> Save
                  </Button>
                  <Button $variant="secondary" onClick={() => setNewQualification(null)}>
                    <FaTimes /> Cancel
                  </Button>
                </ButtonGroup>
              </Card>
            )}
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>Certifications</SectionTitle>
              {editingProfile && (
                <Button onClick={handleAddCertification}>
                  <FaPlus /> Add Certification
                </Button>
              )}
            </SectionHeader>
            <Grid>
              {certifications.map(cert => (
                <Card key={cert.id}>
                  <CardHeader>
                    <div>
                      <CardTitle>{cert.name}</CardTitle>
                      <CardSubtitle>{cert.issuer}</CardSubtitle>
                      <CardSubtitle>Issued: {cert.issue_date}</CardSubtitle>
                      {cert.expiry_date && <CardSubtitle>Expires: {cert.expiry_date}</CardSubtitle>}
                      {cert.credential_url && (
                        <CardSubtitle>
                          <a href={cert.credential_url} target="_blank" rel="noopener noreferrer">View Credential</a>
                        </CardSubtitle>
                      )}
                    </div>
                    {editingProfile && (
                      <IconButton $danger onClick={() => handleDeleteCertification(cert.id)}>
                        <FaTrash />
                      </IconButton>
                    )}
                  </CardHeader>
                </Card>
              ))}
            </Grid>
            {newCertification && (
              <Card style={{ marginTop: '1rem' }}>
                <Grid>
                  <FormGroup>
                    <Label>Certification Name</Label>
                    <Input
                      value={newCertification.name}
                      onChange={(e) => setNewCertification({ ...newCertification, name: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Issuing Organization</Label>
                    <Input
                      value={newCertification.issuer}
                      onChange={(e) => setNewCertification({ ...newCertification, issuer: e.target.value })}
                    />
                  </FormGroup>
                </Grid>
                <Grid>
                  <FormGroup>
                    <Label>Issue Date</Label>
                    <Input
                      type="month"
                      value={newCertification.issue_date}
                      onChange={(e) => setNewCertification({ ...newCertification, issue_date: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Expiry Date (optional)</Label>
                    <Input
                      type="month"
                      value={newCertification.expiry_date}
                      onChange={(e) => setNewCertification({ ...newCertification, expiry_date: e.target.value })}
                    />
                  </FormGroup>
                </Grid>
                <FormGroup>
                  <Label>Credential ID (optional)</Label>
                  <Input
                    value={newCertification.credential_id}
                    onChange={(e) => setNewCertification({ ...newCertification, credential_id: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Credential URL (optional)</Label>
                  <Input
                    value={newCertification.credential_url}
                    onChange={(e) => setNewCertification({ ...newCertification, credential_url: e.target.value })}
                    placeholder="https://..."
                  />
                </FormGroup>
                <ButtonGroup>
                  <Button onClick={handleSaveCertification}>
                    <FaSave /> Save
                  </Button>
                  <Button $variant="secondary" onClick={() => setNewCertification(null)}>
                    <FaTimes /> Cancel
                  </Button>
                </ButtonGroup>
              </Card>
            )}
          </Section>
        </>
      )}

      {activeTab === 'listings' && (
        <Section>
          <SectionHeader>
            <SectionTitle>My Posts</SectionTitle>
          </SectionHeader>
          {loading ? (
            <LoadingContainer>
              <FaSpinner size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '1rem' }}>Loading your posts...</span>
            </LoadingContainer>
          ) : listings.length > 0 ? (
            <Grid>
              {listings.map(listing => (
                <ListingCard key={listing.id}>
                  <CardHeader>
                    <div style={{ flex: 1 }}>
                      <CardTitle>{listing.title}</CardTitle>
                      <CardSubtitle>{listing.type === 'hustle' ? 'Hustle' : 'Gig'}</CardSubtitle>
                      <StatusBadge $status={listing.status}>
                        {listing.status.replace('_', ' ')}
                      </StatusBadge>
                    </div>
                    <ButtonGroup>
                      <IconButton onClick={() => handleEditListing(listing)} title="Edit listing">
                        <FaEdit />
                      </IconButton>
                      <IconButton $danger onClick={() => handleDeleteListing(listing)} title="Delete listing">
                        <FaTrash />
                      </IconButton>
                    </ButtonGroup>
                  </CardHeader>
                  <CardText>{listing.short_description}</CardText>
                  <CardSubtitle>
                    Created: {new Date(listing.created_at).toLocaleDateString()}
                  </CardSubtitle>
                  {listing.status === 'pending_moderation' && (
                    <CardSubtitle style={{ marginTop: '0.5rem', color: '#f39c12' }}>
                      <FaExclamationTriangle style={{ marginRight: '0.3rem' }} />
                      Awaiting admin approval
                    </CardSubtitle>
                  )}
                  {listing.status === 'rejected' && (
                    <>
                      <CardSubtitle style={{ marginTop: '0.5rem', color: '#e74c3c' }}>
                        <FaTimes style={{ marginRight: '0.3rem' }} />
                        This listing was rejected
                      </CardSubtitle>
                      {listing.rejection_reason && (
                        <Card style={{ marginTop: '0.75rem', backgroundColor: '#fee', border: '1px solid #e74c3c' }}>
                          <CardSubtitle style={{ color: '#e74c3c', marginBottom: '0.5rem' }}>
                            <strong>Rejection Reason:</strong>
                          </CardSubtitle>
                          <CardText style={{ color: '#666' }}>{listing.rejection_reason}</CardText>
                        </Card>
                      )}
                    </>
                  )}
                </ListingCard>
              ))}
            </Grid>
          ) : (
            <p>You haven't created any posts yet.</p>
          )}
        </Section>
      )}

      {activeTab === 'moderation' && profile?.role_type === USER_ROLES.ADMIN && (
        <Section>
          <SectionHeader>
            <SectionTitle>Pending Moderation ({pendingListings.length})</SectionTitle>
          </SectionHeader>
          {loading ? (
            <LoadingContainer>
              <FaSpinner size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '1rem' }}>Loading pending listings...</span>
            </LoadingContainer>
          ) : pendingListings.length > 0 ? (
            <>
              {pendingListings.map(listing => (
                <ModerationCard key={listing.id}>
                  <CardHeader>
                    <div>
                      <CardTitle>{listing.title}</CardTitle>
                      <CardSubtitle>
                        {listing.type === 'hustle' ? 'Hustle' : 'Gig'} · 
                        Owner: {listing.profiles?.display_name || listing.profiles?.email}
                      </CardSubtitle>
                      <CardSubtitle>
                        Submitted: {listing.submitted_at ? new Date(listing.submitted_at).toLocaleString() : 'N/A'}
                      </CardSubtitle>
                    </div>
                  </CardHeader>
                  <CardText><strong>Short Description:</strong> {listing.short_description}</CardText>
                  {listing.full_story && (
                    <Card style={{ marginTop: '1rem' }}>
                      <CardTitle style={{ fontSize: '0.9rem' }}>Full Story:</CardTitle>
                      <CardText>{listing.full_story}</CardText>
                    </Card>
                  )}
                  <Grid style={{ marginTop: '1rem' }}>
                    {listing.location && listing.location.province && (
                      <Card>
                        <CardSubtitle><strong>Location:</strong></CardSubtitle>
                        <CardText>
                          {(() => {
                            const loc = listing.location;
                            const displayArea = loc.area === 'Other' ? loc.customArea : loc.area;
                            return displayArea ? `${displayArea}, ${loc.province}` : loc.province;
                          })()}
                        </CardText>
                      </Card>
                    )}
                    {listing.type === 'hustle' && listing.industry && (
                      <Card>
                        <CardSubtitle><strong>Industry:</strong></CardSubtitle>
                        <CardText>{listing.industry}</CardText>
                      </Card>
                    )}
                    {listing.type === 'hustle' && listing.role && (
                      <Card>
                        <CardSubtitle><strong>Role:</strong></CardSubtitle>
                        <CardText>{listing.role}</CardText>
                      </Card>
                    )}
                    {listing.pay && (
                      <Card>
                        <CardSubtitle><strong>Pay:</strong></CardSubtitle>
                        <CardText>{listing.pay}</CardText>
                      </Card>
                    )}
                    <Card>
                      <CardSubtitle><strong>Traveling:</strong></CardSubtitle>
                      <CardText>{listing.traveling ? 'Yes' : 'No'}</CardText>
                    </Card>
                  </Grid>
                  {listing.skills && listing.skills.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <CardSubtitle><strong>Skills:</strong></CardSubtitle>
                      <div>
                        {listing.skills.map(skill => (
                          <SkillBadge key={skill}>{skill}</SkillBadge>
                        ))}
                      </div>
                    </div>
                  )}
                  <ModerationActions>
                    <ApproveButton 
                      onClick={() => handleApproveListing(listing)}
                      disabled={actionLoading === listing.id}
                    >
                      <FaCheck /> Approve
                    </ApproveButton>
                    <RejectButton 
                      onClick={() => setRejectModal({ show: true, listing, reason: '' })}
                      disabled={actionLoading === listing.id}
                    >
                      <FaTimes /> Reject
                    </RejectButton>
                  </ModerationActions>
                </ModerationCard>
              ))}
            </>
          ) : (
            <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
              <FaCheck style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block', margin: '0 auto 1rem' }} />
              No pending listings to moderate
            </p>
          )}
        </Section>
      )}

      {activeTab === 'all-listings' && profile?.role_type === USER_ROLES.ADMIN && (
        <Section>
          <SectionHeader>
            <SectionTitle>All Listings ({allListings.length})</SectionTitle>
          </SectionHeader>
          {loading ? (
            <LoadingContainer>
              <FaSpinner size={24} style={{ animation: 'spin 1s linear infinite' }} />
              <span style={{ marginLeft: '1rem' }}>Loading all listings...</span>
            </LoadingContainer>
          ) : allListings.length > 0 ? (
            <Grid>
              {allListings.map(listing => {
                const expiryInfo = listingService.getExpiryInfo(listing);
                return (
                  <ListingCard key={listing.id}>
                    <CardHeader>
                      <div style={{ flex: 1 }}>
                        <CardTitle>{listing.title}</CardTitle>
                        <CardSubtitle>
                          {listing.type === 'hustle' ? 'Hustle' : 'Gig'} · 
                          Owner: {listing.profiles?.display_name || listing.profiles?.email}
                        </CardSubtitle>
                        <div style={{ marginTop: '0.5rem' }}>
                          <StatusBadge $status={listing.status}>
                            {listing.status.replace('_', ' ')}
                          </StatusBadge>
                          {listing.status === 'active' && listing.expires_at && (
                            <ExpiryBadge 
                              $isExpired={expiryInfo.isExpired} 
                              $isExpiringSoon={expiryInfo.isExpiringSoon}
                            >
                              {expiryInfo.isExpired ? (
                                <>
                                  <FaExclamationCircle /> Expired
                                </>
                              ) : expiryInfo.isExpiringSoon ? (
                                <>
                                  <FaClock /> {expiryInfo.daysRemaining} days left
                                </>
                              ) : (
                                <>
                                  <FaCheck /> {expiryInfo.daysRemaining} days left
                                </>
                              )}
                            </ExpiryBadge>
                          )}
                        </div>
                      </div>
                      <IconButton $danger onClick={() => handleAdminDeleteListing(listing)} title="Delete listing">
                        <FaTrash />
                      </IconButton>
                    </CardHeader>
                    <CardText>{listing.short_description}</CardText>
                    <CardSubtitle>
                      Created: {new Date(listing.created_at).toLocaleDateString()}
                    </CardSubtitle>
                    {listing.approved_at && (
                      <CardSubtitle>
                        Approved: {new Date(listing.approved_at).toLocaleDateString()}
                      </CardSubtitle>
                    )}
                    {listing.expires_at && (
                      <CardSubtitle>
                        Expires: {new Date(listing.expires_at).toLocaleDateString()}
                      </CardSubtitle>
                    )}
                  </ListingCard>
                );
              })}
            </Grid>
          ) : (
            <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
              No listings found
            </p>
          )}
        </Section>
      )}

      {rejectModal.show && (
        <Modal onClick={() => setRejectModal({ show: false, listing: null, reason: '' })}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Reject Listing</h3>
            <FormGroup>
              <Label>Reason for rejection</Label>
              <TextArea
                value={rejectModal.reason}
                onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
                placeholder="Provide a reason for rejecting this listing..."
              />
            </FormGroup>
            <ButtonGroup>
              <Button onClick={handleRejectListing} disabled={!rejectModal.reason.trim()}>
                <FaTimes /> Reject
              </Button>
              <Button 
                $variant="secondary" 
                onClick={() => setRejectModal({ show: false, listing: null, reason: '' })}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Profile;
